// Chart › Finance › Headcount plan (`/c/{slug}/chart/finance/headcount`; 11 §Planning "Headcount
// plan", M5-03 `headcountPlan.*`): planned seats, human and digital together, each with its team
// or seat, months and monthly cost; and the plan for a month against the live org chart (per
// team: planned vs held vs open, and whether each linked line is filled by the planned kind).
// Restricted (the owner and finance admins read; finance admins plan, edit and archive). Calls on
// load: the caller's standing, then the plan and the month's diff; the org chart for names.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  cn,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  StatGrid,
  StatTile,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ClipboardListIcon,
  PencilIcon,
  PlusIcon,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { ConfirmRowAction } from "../confirm-row-action";
import { toMinor } from "../costs/costs-page";
import { formatMoney, fractionDigits } from "../format";
import { LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";
import {
  type Currency,
  CurrencySelect,
  FinancePage,
  FormDialog,
  FormField,
  HOLDER_LABELS,
  MonthSelect,
  monthKeyOf,
  monthLabel,
  monthsAround,
  ReadOnlyNotice,
  RestrictedNotice,
  useFinanceStanding,
  useOrgNames,
} from "./finance-parts";
import { financeKeys, headcountDiffQuery, headcountPlanQuery } from "./finance-queries";

type Plan = Awaited<ReturnType<Api["client"]["headcountPlan"]["list"]>>["data"][number];
type Holder = Plan["holder_kind"];

const LINE_STATUS: Record<
  string,
  { label: string; tone: "success" | "warning" | "danger" | "neutral" }
> = {
  matched: { label: "Filled", tone: "success" },
  holder_mismatch: { label: "Other holder kind", tone: "warning" },
  unfilled: { label: "Seat open", tone: "danger" },
  planned: { label: "No seat yet", tone: "neutral" },
};

const signedCount = (n: number) => (n > 0 ? `+${n}` : String(n));

export function HeadcountScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 6, 12), [current]);
  const [month, setMonth] = useState(current);
  const standing = useFinanceStanding(api, company);
  const plan = useInfiniteQuery({
    ...headcountPlanQuery(api, company.id),
    enabled: standing.restricted,
  });
  const diff = useQuery({
    ...headcountDiffQuery(api, company.id, month),
    enabled: standing.restricted,
  });
  const names = useOrgNames(api, company);
  const [editing, setEditing] = useState<Plan | "new" | null>(null);
  const rows = plan.data?.pages.flatMap((p) => p.data) ?? [];
  const qc = useQueryClient();
  const archive = useMutation({
    mutationFn: (row: Plan) =>
      api.client.headcountPlan.update({
        params: { co: company.id, id: row.id },
        body: { status: row.status === "active" ? "archived" : "active" },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: financeKeys.all(company.id) }),
  });

  return (
    <FinancePage
      company={company}
      where="chart"
      title="Headcount plan"
      icon={ClipboardListIcon}
      {...(plan.data ? { count: rows.length } : {})}
      actions={
        standing.admin ? (
          <Button size="sm" onClick={() => setEditing("new")}>
            <PlusIcon aria-hidden="true" />
            Plan a seat
          </Button>
        ) : null
      }
    >
      {standing.query.isPending ? (
        <Loading label="Loading your finance access" rows={2} />
      ) : standing.query.isError ? (
        <LoadError what="Your finance access" error={standing.query.error} />
      ) : !standing.restricted ? (
        <RestrictedNotice company={company} what="The headcount plan and its costs" />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          {!standing.admin ? <ReadOnlyNotice company={company} owner={standing.owner} /> : null}
          {plan.isPending ? (
            <Loading label="Loading the plan" rows={3} />
          ) : plan.isError ? (
            <LoadError what="The plan" error={plan.error} />
          ) : rows.length === 0 ? (
            <EmptyState
              variant="card"
              icon={ClipboardListIcon}
              title="Nothing planned yet"
              description="Plan the seats you mean to add, and see them against the org chart month by month."
            />
          ) : (
            <>
              <Table data-slot="headcount-plan">
                <TableCaption>
                  Planned seats; archived lines leave the month's comparison.
                </TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead scope="col">Seat</TableHead>
                    <TableHead scope="col" className="max-md:hidden">
                      Team or seat
                    </TableHead>
                    <TableHead scope="col">Holder</TableHead>
                    <TableHead scope="col" className="max-sm:hidden">
                      Months
                    </TableHead>
                    <TableHead scope="col" className="text-right">
                      Monthly cost
                    </TableHead>
                    <TableHead scope="col">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((l) => (
                    <TableRow
                      key={l.id}
                      data-plan={l.id}
                      className={cn("group/row", l.status === "archived" && "opacity-60")}
                    >
                      <TableHead scope="row" className="font-normal">
                        {l.title}
                        {l.status === "archived" ? (
                          <Badge variant="neutral" className="ml-2">
                            Archived
                          </Badge>
                        ) : null}
                      </TableHead>
                      <TableCell className="max-md:hidden">
                        {l.seat_id
                          ? names.seatName(l.seat_id)
                          : l.team_id
                            ? names.teamName(l.team_id)
                            : "Company"}
                      </TableCell>
                      <TableCell>{HOLDER_LABELS[l.holder_kind]}</TableCell>
                      <TableCell className="max-sm:hidden">
                        {monthLabel(l.start_month)}
                        {l.end_month ? ` – ${monthLabel(l.end_month)}` : " onwards"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatMoney(l.monthly_cost_minor, l.currency)}
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {standing.admin ? (
                          <RowActions>
                            <RowAction
                              icon={PencilIcon}
                              label={`Edit ${l.title}`}
                              tip="Edit"
                              onClick={() => setEditing(l)}
                            />
                            {l.status === "active" ? (
                              <ConfirmRowAction
                                icon={ArchiveIcon}
                                label={`Archive ${l.title}`}
                                tip="Archive"
                                disabled={archive.isPending}
                                title={`Archive “${l.title}”?`}
                                description="It leaves the plan and its totals. Restore it any time."
                                confirmLabel="Archive"
                                onConfirm={() => archive.mutate(l)}
                              />
                            ) : (
                              <RowAction
                                icon={ArchiveRestoreIcon}
                                label={`Restore ${l.title}`}
                                tip="Restore"
                                disabled={archive.isPending}
                                onClick={() => archive.mutate(l)}
                              />
                            )}
                          </RowActions>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <LoadMore
                what="planned seats"
                hasMore={plan.hasNextPage}
                loading={plan.isFetchingNextPage}
                onMore={() => void plan.fetchNextPage()}
              />
            </>
          )}
          <Section
            id="headcount-diff"
            title="Plan vs org chart"
            description="Planned seats active in the month against the seats held today."
            actions={<MonthSelect value={month} onChange={setMonth} months={months} />}
          >
            {diff.isPending ? (
              <Loading label="Loading the comparison" rows={3} />
            ) : diff.isError ? (
              <LoadError what="The comparison" error={diff.error} />
            ) : (
              <>
                <StatGrid data-slot="headcount-totals" aria-label={`${monthLabel(month)} plan`}>
                  <StatTile
                    label="Planned cost"
                    value={formatMoney(diff.data.planned_cost_minor, diff.data.currency)}
                    detail={`${monthLabel(month)}, a month`}
                  />
                  <StatTile
                    label="Planned seats"
                    value={String(diff.data.lines.length)}
                    detail={`${diff.data.lines.filter((x) => x.status === "matched").length} filled`}
                  />
                </StatGrid>
                <Table data-slot="headcount-diff">
                  <TableCaption>
                    Planned and held seats per team (a seat counts in its first team). Difference is
                    planned minus held.
                  </TableCaption>
                  <TableHeader>
                    <TableRow>
                      <TableHead scope="col">Team</TableHead>
                      <TableHead scope="col" className="text-right">
                        People planned / held
                      </TableHead>
                      <TableHead scope="col" className="text-right">
                        DEs planned / held
                      </TableHead>
                      <TableHead scope="col" className="text-right max-sm:hidden">
                        Open seats
                      </TableHead>
                      <TableHead scope="col" className="text-right">
                        Difference
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {diff.data.teams.map((t) => (
                      <TableRow key={t.team_id ?? "company"} data-team={t.team_id ?? "company"}>
                        <TableHead scope="row" className="font-normal">
                          {t.name}
                        </TableHead>
                        <TableCell className="text-right tabular-nums">
                          {t.planned.person} / {t.live.person}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {t.planned.digital_employee} / {t.live.digital_employee}
                        </TableCell>
                        <TableCell className="text-right tabular-nums max-sm:hidden">
                          {t.live.open}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {signedCount(t.delta.person)} people,{" "}
                          {signedCount(t.delta.digital_employee)} DEs
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {diff.data.lines.length ? (
                  <ul className="flex flex-col gap-1.5 text-sm" data-slot="headcount-lines">
                    {diff.data.lines.map((l) => {
                      const s = LINE_STATUS[l.status] ?? LINE_STATUS.planned;
                      return (
                        <li key={l.id} className="flex flex-wrap items-center gap-2">
                          <Badge variant={s?.tone ?? "neutral"}>{s?.label}</Badge>
                          {l.title}
                          {l.seat_id ? (
                            <span className="text-muted-foreground">
                              · {names.seatName(l.seat_id)}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
              </>
            )}
          </Section>
        </div>
      )}
      {editing ? (
        <PlanDialog
          api={api}
          company={company}
          row={editing === "new" ? null : editing}
          teams={names.teams}
          seats={names.seats}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </FinancePage>
  );
}

const major = (minor: number, currency: string) =>
  (minor / 10 ** fractionDigits(currency)).toFixed(fractionDigits(currency));

function PlanDialog({
  api,
  company,
  row,
  teams,
  seats,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  row: Plan | null;
  teams: { id: string; name: string }[];
  seats: { id: string; title: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const current = monthKeyOf(new Date());
  const months = useMemo(() => monthsAround(current, 6, 24), [current]);
  const [title, setTitle] = useState(row?.title ?? "");
  const [holder, setHolder] = useState<Holder>(row?.holder_kind ?? "digital_employee");
  const [where, setWhere] = useState(
    row?.seat_id ? `seat:${row.seat_id}` : row?.team_id ? `team:${row.team_id}` : "",
  );
  const [start, setStart] = useState(row?.start_month ?? current);
  const [end, setEnd] = useState(row?.end_month ?? "");
  const [currency, setCurrency] = useState<Currency>((row?.currency as Currency) ?? "USD");
  const [cost, setCost] = useState(row ? major(row.monthly_cost_minor, row.currency) : "");
  const [note, setNote] = useState(row?.note ?? "");
  const ids = {
    title: useId(),
    holder: useId(),
    where: useId(),
    start: useId(),
    end: useId(),
    cur: useId(),
    cost: useId(),
    note: useId(),
  };
  const minor = toMinor(cost, currency);
  const body = {
    title: title.trim(),
    holder_kind: holder,
    team_id: where.startsWith("team:") ? where.slice(5) : null,
    seat_id: where.startsWith("seat:") ? where.slice(5) : null,
    start_month: start,
    end_month: end || null,
    monthly_cost_minor: minor ?? 0,
    currency,
    note: note.trim() || null,
  };
  const save = useMutation({
    mutationFn: () =>
      row
        ? api.client.headcountPlan.update({ params: { co: company.id, id: row.id }, body })
        : api.client.headcountPlan.create({
            params: { co: company.id },
            idempotencyKey: newIdempotencyKey(),
            body,
          }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title={row ? "Edit planned seat" : "Plan a seat"}
      description="A planned seat with its holder kind, its months and what it will cost a month."
      submitLabel={row ? "Save" : "Plan seat"}
      pending={save.isPending}
      error={save.error}
      canSubmit={title.trim().length > 0 && minor !== null && minor >= 0 && (!end || end >= start)}
      onSubmit={() => save.mutate()}
    >
      <FormField id={ids.title} label="Seat title">
        <Input
          id={ids.title}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.holder} label="Held by">
          <NativeSelect
            id={ids.holder}
            value={holder}
            onChange={(e) => setHolder(e.target.value as Holder)}
          >
            <NativeSelectOption value="person">{HOLDER_LABELS.person}</NativeSelectOption>
            <NativeSelectOption value="digital_employee">
              {HOLDER_LABELS.digital_employee}
            </NativeSelectOption>
          </NativeSelect>
        </FormField>
        <FormField id={ids.where} label="Team or seat">
          <NativeSelect id={ids.where} value={where} onChange={(e) => setWhere(e.target.value)}>
            <NativeSelectOption value="">Company (no team)</NativeSelectOption>
            {teams.map((t) => (
              <NativeSelectOption key={t.id} value={`team:${t.id}`}>
                Team: {t.name}
              </NativeSelectOption>
            ))}
            {seats.map((s) => (
              <NativeSelectOption key={s.id} value={`seat:${s.id}`}>
                Seat: {s.title}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.start} label="From">
          <MonthSelect id={ids.start} value={start} onChange={setStart} months={months} />
        </FormField>
        <FormField
          id={ids.end}
          label="Until"
          error={end && end < start ? "Ends before it starts." : null}
        >
          <NativeSelect id={ids.end} value={end} onChange={(e) => setEnd(e.target.value)}>
            <NativeSelectOption value="">No end</NativeSelectOption>
            {months.map((m) => (
              <NativeSelectOption key={m} value={m}>
                {monthLabel(m)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField
          id={ids.cost}
          label="Monthly cost"
          error={cost !== "" && minor === null ? "Enter an amount like 4500.00." : null}
        >
          <Input
            id={ids.cost}
            inputMode="decimal"
            required
            value={cost}
            onChange={(e) => setCost(e.target.value)}
          />
        </FormField>
        <FormField id={ids.cur} label="Currency">
          <CurrencySelect id={ids.cur} value={currency} onChange={setCurrency} />
        </FormField>
      </div>
      <FormField id={ids.note} label="Note">
        <Input
          id={ids.note}
          maxLength={2000}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormField>
    </FormDialog>
  );
}
